import { Map } from "immutable";

import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import syncDocumentTypes from "../../constants/syncDocumentTypes";
import syncFieldTypes from "../../constants/syncFieldTypes";
import syncLocalFields from "../../constants/syncLocalFields";
import { SYNC_NARRATIVE_FOLDER_IDENTIFIER } from "../../constants/syncNarrativeFolderIdentifier";
import { syncStatusOptions } from "../../constants/syncStatus";
import { Precision } from "../../types";
import { doSetsIntersect } from "../../utils/immutableExtensions";
import dateWithComponents from "../definition/calendar/dateFunctions/dateWithComponents";
import { getCalendarIsRelativeFromCalendar, isGregorianCalendarFromCalendar } from "../definition/calendar/selectors";
import { referenceSelectors } from "../definition/references";
import { typeSelectors } from "../definition/types";
import itemSelectors from "../items/selectors";

const getSyncedLocalToExternalIds = state => state.sync.localToExternalIds;

// Note: We do not remove localToExternalIds when items are deleted, because sync is excluded
// from Undo/Redo, which means the link would not be recovered if the user undid the change
// (we should possibly consider changing that).
// So for now, we strip them out here, as well as on file load (via repairSyncRecord), because
// we do not need to worry about the Undo chain by then...
const getLocalToExternalIds = state =>
  state.sync.localToExternalIds
    .merge(state.sync.userCreatedLinks)
    .filter((externalId, localId) => itemSelectors.getItemsById(state).has(localId));

const getCustomFieldsIdsMap = state => state.sync.customFieldsIdsMap;

const getSyncProjectIsScrivener2 = state => state.sync && state.sync.isScrivener2;
const getSyncFileMissing = state => state.sync && state.sync.fileMissing;
const getExternalSyncRep = state => state.sync.externalRep;
const getLastSyncRep = state => state.sync.lastSyncRep;
const getSyncSettings = state => state.sync.settings;
const getSyncStatus = state => state.sync.status;
const getSyncUserActions = state => state.sync.userActions;
const getSyncIgnoreExternalIds = state => state.sync.ignoreExternalIds;
const getUpdatedSettingsOrSettings = state =>
  state.sync.status.updatedSettings ? state.sync.status.updatedSettings : state.sync.settings;

const getDocumentTree = state => state.sync.externalRep.documentTree;
const getDocumentMap = state => state.sync.externalRep.documents;

const getNarrativeFolderIdFromSyncSettings = syncSettings => syncSettings.narrativeFolderId;
const getNarrativeFolderLabelFromSyncRep = (rep, syncSettings) => {
  let doc = null;
  if (rep && rep.documents && rep.documents.has(syncSettings.narrativeFolderId)) {
    doc = rep.documents.get(syncSettings.narrativeFolderId);
  }
  if (rep && rep.documents && rep.documents.has(SYNC_NARRATIVE_FOLDER_IDENTIFIER)) {
    doc = rep.documents.get(SYNC_NARRATIVE_FOLDER_IDENTIFIER);
  }
  if (doc) {
    return doc.label;
  }
  return null;
};

const getSyncNarrativeFolderId = state => getNarrativeFolderIdFromSyncSettings(state.sync.settings);
const getSyncNarrativeFolderLabel = state =>
  getNarrativeFolderLabelFromSyncRep(state.sync.externalRep, state.sync.settings);

const getCustomFields = state => state.sync.externalRep.customFields;
const getCustomFieldIndex = (state, fieldId) => state.sync.externalRep.customFieldsOrder.indexOf(fieldId);
const getCustomFieldsOfType = (state, type) =>
  getCustomFields(state)
    .filter(f => f.type === type)
    .sortBy(f => getCustomFieldIndex(state, f.id));
const getCustomFieldsOfTypes = (state, types) =>
  getCustomFields(state)
    .filter(f => types.indexOf(f.type) >= 0)
    .sortBy(f => getCustomFieldIndex(state, f.id));

const getCustomDateFields = state => getCustomFieldsOfType(state, syncFieldTypes.DATE);
const getCustomTextFields = state => getCustomFieldsOfType(state, syncFieldTypes.TEXT);
const getCustomMultitextFields = state => getCustomFieldsOfType(state, syncFieldTypes.MULTITEXT);
const getCustomCheckboxFields = state => getCustomFieldsOfType(state, syncFieldTypes.CHECKBOX);
const getCustomListFields = state => getCustomFieldsOfType(state, syncFieldTypes.LIST);

const getGenericRootFolders = state => {
  const documentMap = getDocumentMap(state);
  const tree = getDocumentTree(state);
  return Map(
    tree.children
      .filter(d => documentMap.get(d.id).type === syncDocumentTypes.FOLDER)
      .map(d => [d.id, documentMap.get(d.id).label])
      .toArray()
  );
};

const getSyncKeywordReferenceOptionsForTypeIds = (state, typeIds) => {
  // get all reference options associated with these types
  const options = referenceSelectors.getOrderedReferencesForTypeIds(state, typeIds);
  // But we need to trim this list further...
  // We only want those that target non-narrative/sequence types -- which is the same as the keywordTypes list
  const keywordTypes = typeSelectors
    .getSyncToEntityTypes(state)
    .map(t => t.id)
    .toSet();
  // Filter the list
  return options.filter(r => doSetsIntersect(r.types.toSet(), keywordTypes));
};

const getItemTypesBySyncId = state => {
  const itemTypesById = itemSelectors.getItemsById(state).map(i => i.type);
  const localToExternalIds = getLocalToExternalIds(state);
  let result = {};
  localToExternalIds.forEach((eId, lId) => {
    result[eId] = itemTypesById.get(lId);
  });
  return Map(result);
};

const getSyncLabelForId = (definitions, id) => {
  switch (id) {
    case syncLocalFields.start:
      return "Start Date";
    case syncLocalFields.end:
      return "End Date";
    case syncLocalFields.duration:
      return "Duration";
    case syncLocalFields.color:
      return "Color";
  }
  const typesById = definitions.types.byId;
  const type = typesById.get(id);
  if (type) {
    return type.pluralLabel;
  }

  const propertiesById = definitions.properties.byId;
  const prop = propertiesById.get(id);
  if (prop) {
    return prop.label;
  }

  const referencesById = definitions.references.byId;
  const ref = referencesById.get(id);
  if (ref) {
    return ref.label;
  }

  return null;
};

const getSyncFieldTypeForProperty = property => {
  switch (property.format) {
    case PropertyFormatIdentifier.boolean:
      return syncFieldTypes.CHECKBOX;
    case PropertyFormatIdentifier.currency:
    case PropertyFormatIdentifier.double:
    case PropertyFormatIdentifier.integer:
    case PropertyFormatIdentifier.percentage:
      return syncFieldTypes.TEXT;
    case PropertyFormatIdentifier.list:
      return syncFieldTypes.LIST;
    case PropertyFormatIdentifier.text:
      return syncFieldTypes.TEXT;
    case PropertyFormatIdentifier.multitext:
      return syncFieldTypes.MULTITEXT;
    default:
      return null;
  }
};

const isLegalTimestampRange = (calendar, timestampRange) => {
  if (timestampRange.min < 0) {
    return false;
  }
  const allowedMax = dateWithComponents(calendar, 1, 9999, 12, 31, 23, 59, 59, Precision.Second);
  return timestampRange.max < allowedMax.timestamp;
};

const getSyncFieldTypesForCalendarAndRange = (calendar, timestampRange, isScrivener2) => {
  const isGregorian = isGregorianCalendarFromCalendar(calendar);
  const isRelative = getCalendarIsRelativeFromCalendar(calendar);
  const isLegal = isLegalTimestampRange(calendar, timestampRange);
  return isGregorian && !isRelative && isLegal && !isScrivener2
    ? [syncFieldTypes.DATE, syncFieldTypes.TEXT]
    : [syncFieldTypes.TEXT];
};

const getSyncFieldTypeForId = (definitions, timestampRange, id, isScrivener2) => {
  switch (id) {
    case syncLocalFields.start:
    case syncLocalFields.end:
      return getSyncFieldTypesForCalendarAndRange(definitions.calendar, timestampRange, isScrivener2)[0];
    case syncLocalFields.duration:
      return syncFieldTypes.TEXT;
    case syncLocalFields.color:
      return syncFieldTypes.LIST;
  }
  const propertiesById = definitions.properties.byId;
  const prop = propertiesById.get(id);
  if (prop) {
    return getSyncFieldTypeForProperty(prop);
  }
  const referencesById = definitions.references.byId;
  const ref = referencesById.get(id);
  if (ref) {
    return syncFieldTypes.MULTITEXT;
  }
  return null;
};

const isSyncFieldTypeList = (propertiesById, id) => {
  switch (id) {
    case syncLocalFields.start:
    case syncLocalFields.end:
    case syncLocalFields.duration:
      return false;
    case syncLocalFields.color:
      return true;
  }
  const prop = propertiesById.get(id);
  if (prop) {
    return getSyncFieldTypeForProperty(prop) === syncFieldTypes.LIST;
  }
  return false;
};

const getSyncStatusLabelForStatus = (status, syncApplicationName) => {
  let statusLabel = "";
  switch (status) {
    case syncStatusOptions.DETECTED_CONFLICT: {
      statusLabel = "Conflict";
      break;
    }
    case syncStatusOptions.DETECTED_CHANGE_IN_LOCAL: {
      statusLabel = "Changed in Timeline";
      break;
    }
    case syncStatusOptions.DETECTED_CHANGE_IN_EXTERNAL: {
      statusLabel = `Changed in ${syncApplicationName}`;
      break;
    }
    case syncStatusOptions.DETECTED_CHANGE_IN_BOTH: {
      statusLabel = `Changed in Timeline and ${syncApplicationName}`;
      break;
    }
    case syncStatusOptions.DETECTED_DELETE_IN_LOCAL: {
      statusLabel = "Deleted from Narrative";
      break;
    }
    case syncStatusOptions.DETECTED_DELETE_IN_EXTERNAL: {
      statusLabel = `Deleted from  ${syncApplicationName}`;
      break;
    }
    case syncStatusOptions.DETECTED_CREATE_IN_LOCAL: {
      statusLabel = "New in Narrative";
      break;
    }
    case syncStatusOptions.DETECTED_CREATE_IN_EXTERNAL: {
      statusLabel = `New in  ${syncApplicationName}`;
      break;
    }
    case syncStatusOptions.DETECTED_MOVE_IN_LOCAL: {
      // statusLabel = "Moved in Narrative";
      statusLabel = "Moved to new position";
      break;
    }
    case syncStatusOptions.DETECTED_MOVE_IN_EXTERNAL: {
      // statusLabel = `Moved in ${syncApplicationName}`;
      statusLabel = `Moved to new position`;
      break;
    }
    case syncStatusOptions.DETECTED_NO_CHANGE: {
      statusLabel = "No changes";
      break;
    }
  }

  return statusLabel;
};

export default {
  getSyncSettings,
  getSyncStatus,
  getSyncUserActions,
  getUpdatedSettingsOrSettings,
  getSyncFileMissing,
  getSyncProjectIsScrivener2,
  getSyncIgnoreExternalIds,
  getExternalSyncRep,
  getLastSyncRep,
  getSyncedLocalToExternalIds,
  getLocalToExternalIds,
  getCustomFieldsIdsMap,
  getDocumentTree,
  getDocumentMap,
  getGenericRootFolders,
  getCustomFields,
  getCustomFieldsOfType,
  getCustomFieldsOfTypes,
  getCustomDateFields,
  getCustomTextFields,
  getCustomMultitextFields,
  getCustomCheckboxFields,
  getCustomListFields,
  getSyncKeywordReferenceOptionsForTypeIds,
  getSyncLabelForId,
  getSyncFieldTypeForId,
  isSyncFieldTypeList,
  getSyncFieldTypeForProperty,
  getSyncFieldTypesForCalendarAndRange,
  getSyncNarrativeFolderId,
  getSyncNarrativeFolderLabel,
  getNarrativeFolderIdFromSyncSettings,
  getNarrativeFolderLabelFromSyncRep,
  getSyncStatusLabelForStatus,
  getItemTypesBySyncId
};
